<script setup lang="ts">
import { ScalarMarkdown } from '@scalar/components/markdown'
import { ScalarPopover } from '@scalar/components/popover'
import { ScalarIconInfo, ScalarIconWarning } from '@scalar/icons'
import type { SchemaObject } from '@scalar/workspace-store/schemas/v3.2/strict/openapi-document'
import { computed } from 'vue'

import { validateParameter } from '@/v2/blocks/request-block/helpers/validate-parameter'
import { useLocalization } from '@/v2/features/localization'

const { schema, value, description } = defineProps<{
  schema?: SchemaObject
  value: string | File | null
  description?: string
}>()

const { translate } = useLocalization()

const invalidParameterMessage = computed(() => validateParameter(schema, value))
const isInvalid = computed(() => invalidParameterMessage.value.ok === false)
</script>
<template>
  <ScalarPopover
    :offset="4"
    placement="left"
    teleport>
    <button
      :aria-label="
        isInvalid
          ? translate('apiClient.requestTableTooltip.invalid')
          : translate('apiClient.requestTableTooltip.moreInformation')
      "
      class="text-c-2 hover:text-c-1 hover:bg-b-2 rounded p-1"
      :role="isInvalid ? 'alert' : 'none'"
      type="button">
      <ScalarIconWarning
        v-if="isInvalid"
        class="text-orange size-3.5 brightness-90 hover:brightness-75" />
      <ScalarIconInfo
        v-else
        class="text-c-2 hover:text-c-1 size-3.5" />
    </button>
    <template #popover>
      <div
        class="text-xxs text-c-1 grid w-fit max-w-xs min-w-0 gap-1.5 rounded px-1.5 pt-2 pb-1.5 leading-none break-words"
        data-testid="request-table-tooltip-content">
        <div
          v-if="invalidParameterMessage.ok === false"
          class="text-error-1">
          {{ invalidParameterMessage.message }}
        </div>
        <div
          v-else-if="
            schema &&
            ('type' in schema ||
              'format' in schema ||
              'minimum' in schema ||
              'maximum' in schema ||
              'default' in schema)
          "
          class="schema text-c-2 truncate *:not-first:before:content-['_·_']">
          <span v-if="'type' in schema">{{ schema.type }}</span>
          <span v-if="'format' in schema">{{ schema.format }}</span>
          <span v-if="'minimum' in schema"
            >{{ translate('apiClient.requestTableTooltip.min') }}
            {{ schema.minimum }}</span
          >
          <span v-if="'maximum' in schema"
            >{{ translate('apiClient.requestTableTooltip.max') }}
            {{ schema.maximum }}</span
          >
          <span v-if="'default' in schema"
            >{{ translate('apiClient.requestTableTooltip.default') }}
            {{ schema.default }}</span
          >
        </div>
        <ScalarMarkdown
          v-if="description && !isInvalid"
          :value="description" />
      </div>
    </template>
  </ScalarPopover>
</template>
